// --------------------------------------------------------------------------
// Header of CPT page
// --------------------------------------------------------------------------

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import axiosAuthInstance from '../../context/security';
import { GeoJSON as GeoJSONType } from 'geojson';
import { Box, Flex, Spacer, Image, IconButton, Heading, Text, Input, Textarea,
  Drawer, DrawerOverlay, DrawerCloseButton, CardFooter, Modal, ModalOverlay,
  DrawerHeader, DrawerContent, DrawerBody, DrawerFooter, ModalContent,
  Button, Breadcrumb, BreadcrumbItem, BreadcrumbLink, useDisclosure,
  FormControl, FormLabel, Card, CardBody, CardHeader, VStack, Avatar, ButtonGroup,  
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, AlertDialog, AlertDialogOverlay,
  AlertDialogContent, AlertDialogCloseButton, AlertDialogHeader, AlertDialogBody,
  AlertDialogFooter, useTheme, useToast } from "@chakra-ui/react";
import { Menu, Delete, Input as InputIcon, Logout, Add } from "@mui/icons-material";
import { useAuth } from '../../context/security';
import { useCPTContext } from '../../context/cpt_context';
import logo_emi from "../assets/emi_inverted.png";
import "../styles.css";


const HeaderCPT: React.FC = () => {

  // 1. State, hooks, event handling and helper functions Header
  const theme = useTheme();
  const toast = useToast();
  const lastRef = React.useRef<HTMLButtonElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()
  const { isOpen: isDBModalOpen, onOpen: onDBModalOpen, onClose: onDBModalClose } = useDisclosure()
  const { isOpen: isDBAlertOpen, onOpen: onDBAlertOpen, onClose: onDBAlertClose } = useDisclosure()
  const { clearAuthToken } = useAuth();
  const [userData, setUserData] = useState<UserData>({ user_info: { name: "",
    preferences: {},}});
  const [prefNameInput, setPrefNameInput] = useState("");
  const [prefDescriptionInput, setPrefDescriptionInput] = useState("");
  const {geojsonDataInput, startDateInput, endDateInput, resolutionInput,
    selectedPlatforms, selectedSpectra, srtInput, cloudInput, selectedSensorClasses,
    setGeojsonDataInput, setStartDateInput, setSRTInput, setCloudInput,
    setEndDateInput, setResolutionInput, setSelectedPlatforms,
    setSelectedSpectra, setSelectedSensorClasses } = useCPTContext();
  const [currentPrefKey, setCurrentPrefKey] = useState("");
  const [errorDBUser, setErrorDBUser] = useState('');

  // Handle acquisition of data from database via backend
  interface ValueItem {
    start_date: string;
    end_date: string;
    area_of_interest: GeoJSONType;
    platforms: string[];
    spectral_range: string[];   
    sensor_class: string;   
    min_resolution: number | null;
    weather_info: boolean;
    srt_info: boolean;
  }

  interface PrefDetails {
    values: ValueItem;
    description: string;
  }

  interface PrefItem {
    [prefNameKey: string]: PrefDetails;
  }

  interface UserData {
    user_info: {
      name: string;
      preferences: {[toolNameKey: string]: PrefItem};
    };
  }

  // Handle error response
  interface ErrorDetail {
    msg: string;
  }
  interface ErrorResponse {
    message: string;
    detail?: ErrorDetail[];
  }

  // Fetch user data from database
  const fetchUserData = useCallback(async () => {
    setErrorDBUser('');
    try {
      const responseUser = await axiosAuthInstance.get('/db/user-info');
      setUserData(responseUser.data);
    } catch (error) {
      setErrorDBUser("Could not fetch data from database. Please log out and in again.")
      toast({
        title: "User Database Error.",
        description: 'Could not fetch data from database. Please log out and in again.',
        status: "error",
        duration: 20000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Fetch data on component mount
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Get total amount of CPT related saved queries
  const countSavedPreferences = (preferences: {[toolNamekey: string]: PrefItem}) => {
    return Object.entries(preferences).reduce((count, [toolNameKey, prefItem]) => {
      if (toolNameKey === "CPT") {
        count += Object.keys(prefItem).length;
      }
      return count;
    }, 0);
  };

  // Handle input for preference name and description
  const handlePrefNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrefNameInput(event.target.value);
  };

  const handlePrefDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrefDescriptionInput(event.target.value);
  };

  // Handle saving a favorite query set.
  const handleSavePref = async () => {
    const prefValues = {
      start_date: startDateInput,
      end_date: endDateInput,
      area_of_interest: geojsonDataInput,
      platforms: selectedPlatforms,
      spectral_range: selectedSpectra,
      sensor_class: selectedSensorClasses,
      min_resolution: resolutionInput,
      weather_info: cloudInput,
      srt_info: srtInput
    };

    try {
      // Request
      const response = await axiosAuthInstance.post('/db/user-settings', {
        task_option: "add",
        pref_name: prefNameInput,
        pref_description: prefDescriptionInput,
        pref_values: prefValues,
        pref_tool: "CPT",
      });
      
      // Handle the response
      if (response.status === 201) {
        fetchUserData();
        toast({
          title: "User Database Updated.",
          description: 'Saved preferences successfully.',
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      // Handle errors
      let errorMessage = 'Could not save preferences.';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>;
        errorMessage = axiosError.message;
        if (axiosError.response && axiosError.response.statusText){
          errorMessage += `: ${axiosError.response.statusText}`;
        }
        if (axiosError.response && axiosError.response.data &&
            axiosError.response.data.detail &&
            axiosError.response.data.detail.length > 0) {
          errorMessage += ` - ${axiosError.response.data.detail}`;
        }  
      }
      toast({
        title: "User Database Error.",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDBModalClose();
    } 
  };

  // Handle deleting a saved query set
  const handleDeletePref = async () => {
    try {
      const response = await axiosAuthInstance.post('/db/user-settings', {
        task_option: "del",
        pref_name: currentPrefKey,
        pref_tool: "CPT",
      });

      // Handle the response
      if (response.status === 201) {
        fetchUserData();
        toast({
          title: "User Database Updated.",
          description: 'Deleted preferences successfully.',
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "User Database Error.",
        description: 'Could not delete preferences.',
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDBAlertClose();
    } 
  };

  // Handle loading set of query parameters from database
  const handleLoadPref = (toolNameKey: string, prefNameKey: string) => {
    const prefItem = userData.user_info.preferences[toolNameKey];
    if (prefItem && prefItem[prefNameKey]) {
      const prefDetails = prefItem[prefNameKey];
      const parsedValues = typeof prefDetails.values === 'string' ? JSON.parse(prefDetails.values) : prefDetails.values;
      setGeojsonDataInput(parsedValues.area_of_interest);
      setStartDateInput(parsedValues.start_date);
      setEndDateInput(parsedValues.end_date);
      setSelectedPlatforms(parsedValues.platforms);
      setSelectedSpectra(parsedValues.spectral_range);
      setSelectedSensorClasses(parsedValues.sensor_class);
      setResolutionInput(parsedValues.min_resolution);
      setSRTInput(parsedValues.srt_info);
      setCloudInput(parsedValues.weather_info);
      toast({
        title: "Set New Parameters.",
        description: 'Successfully updated input parameters from saved preferences.',
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Handle Alert Display when deleting saved queries. Ensures that
  // only one alert box is shown regardless how many queries have been saved
  const handleAlertOpen = (prefKey: string) => {
    setCurrentPrefKey(prefKey);
    onDBAlertOpen();
  };

  // Handle log out and remove token from browser storage
  const handleLogout = () => {
    clearAuthToken(); 
    navigate('/');
  };

  // 2. JSX Header
  return(
    <Flex px={4} alignItems="center" className="header-tools">
      <Flex alignItems="center" height="100%" gap={12}>
        <Image
          src={logo_emi} alt="Logo EMI"
          width="auto" height="70%"/>
        <Breadcrumb>
          <BreadcrumbItem color="white" fontFamily="label">
            <BreadcrumbLink as={Link} to="/main">Main</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem color="white" fontFamily="label">
            <BreadcrumbLink as={Link} to="/cpt">CPT</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </Flex>
      <Spacer />
      <IconButton
        variant="plain" aria-label="Menu"
        size="lg" color="white"
        icon={<Menu />}
        ref={lastRef} onClick={onDrawerOpen}/>
      <Drawer
        isOpen={isDrawerOpen} placement='right'
        onClose={onDrawerClose} finalFocusRef={lastRef}>
        <DrawerOverlay />
        <DrawerContent bg={theme.colors.main.drawer} color="white">
          <DrawerCloseButton />
          <DrawerHeader pb={0}>
            <Flex flex='1' gap='4' alignItems='center' flexWrap='wrap'>
              <Avatar
                name={sessionStorage.getItem("username") || "Unknown"}
                bg={theme.colors.main.highlightLight}
                color={theme.colors.main.highlightDark}/>
              <Box>
                <Heading fontSize={theme.fontSize.heading}>
                  {sessionStorage.getItem("username") || "Unknown"}
                </Heading>
                <Text fontSize={theme.fontSize.info}>
                  Premium Account
                </Text>
              </Box> 
            </Flex>
            <VStack align="left">
             <Button
               mt={10} mb={6} leftIcon={<Add fontSize={theme.fontSize.iconLeft}/>}
               onClick={onDBModalOpen}>
               Save Current Query
             </Button>
             <Modal isOpen={isDBModalOpen} onClose={onDBModalClose}>
              <ModalOverlay />
              <ModalContent bg={theme.colors.main.box} color="white">
                <ModalHeader
                  fontFamily="heading" fontSize={theme.fontSize.alertName}>
                  Save Your Favorite Search Request
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <FormControl>
                    <FormLabel>
                      Insert Name
                    </FormLabel>
                    <Input
                      onChange={handlePrefNameChange}
                      placeholder='Feasibility_Study' />
                  </FormControl>
                  <FormControl>
                    <FormLabel mt={6}>
                      Add a Description
                    </FormLabel>
                    <Textarea
                      placeholder='Add details about the query...'
                      height="200px"
                      className="discreet-scrollbar"
                      onChange={handlePrefDescriptionChange} />
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button onClick={handleSavePref} variant="standard" mr={3}>
                    Save
                  </Button>
                  <Button onClick={onDBModalClose}>Cancel</Button>
                </ModalFooter>
              </ModalContent>
             </Modal>
            </VStack>
            <Flex>
              <Heading fontSize={theme.fontSize.topic}>
                Favorites
              </Heading>
              <Spacer />
              <Text fontFamily="heading" fontSize={theme.fontSize.body}>
                {userData.user_info ? countSavedPreferences(userData.user_info.preferences) : 0}
              </Text>
            </Flex>
          </DrawerHeader>
          {/* NOTE: Box height depends on height of header and footer of drawer*/}
          <DrawerBody pt={0}
            maxHeight="calc(100vh - (158 + 64))" overflowY="auto"
            className="discreet-scrollbar">
            {errorDBUser &&
              <Text
              mt={4} color={theme.colors.main.warning}
              fontSize={theme.fontSize.info}>
                {errorDBUser}
              </Text>
            }
            {userData && userData.user_info && Object.keys(userData.user_info.preferences).length > 0 && (
             Object.entries(userData.user_info.preferences).filter(([toolNameKey, prefItem]) => toolNameKey === "CPT")
             .flatMap(([toolNameKey, prefItem]) => 
              Object.entries(prefItem).map(([prefNameKey, prefDetails]) => (
              <Card
                borderRadius={theme.radii.card}
                id={`pref-card-${prefNameKey}`}
                key={prefNameKey}
                bg={theme.colors.main.results}
                color="black"
                width="100%" mt={2}>
                <CardHeader px={2} pt={2} pb={1}>
                  <Heading
                    color="black"
                    fontSize={theme.fontSize.cardName}>
                    {prefNameKey}
                  </Heading>
                </CardHeader>
                <CardBody px={2} py={0}>
                  <Text 
                    color="black"
                    fontSize={theme.fontSize.info}>
                    {prefDetails.description}
                  </Text>
                </CardBody>
                <CardFooter px={2} pt={1} pb={2}>
                  <Flex justifyContent="flex-end" width="100%">
      	            <ButtonGroup spacing="2">
                      <Button
                        aria-label="Load"
                        leftIcon={<InputIcon fontSize={theme.fontSize.iconLeft}/>}
                        onClick={() => handleLoadPref(toolNameKey, prefNameKey)}>
                        Load
                      </Button>
                      <IconButton
                        aria-label="Delete"
                        icon={<Delete fontSize={theme.fontSize.iconSingle}/>}
                        onClick={() => handleAlertOpen(prefNameKey)}/>
                    </ButtonGroup>
                  </Flex>
                </CardFooter>
              </Card>  
            ))))}
            <AlertDialog
              motionPreset='slideInBottom'
              leastDestructiveRef={cancelRef}
              onClose={onDBAlertClose}
              isOpen={isDBAlertOpen}
              isCentered >
              <AlertDialogOverlay />
              <AlertDialogContent bg={theme.colors.main.box} color="white">
                <AlertDialogHeader
                  fontFamily="heading" fontSize={theme.fontSize.alertName}>
                  Delete Search Request:<br />
                  {currentPrefKey}
                </AlertDialogHeader>
                <AlertDialogCloseButton />
                <AlertDialogBody
                  fontFamily="body" fontSize={theme.fontSize.body}>
                  Are you sure? This can't be undone.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button onClick={onDBAlertClose}>
                    No
                  </Button>
                  <Button onClick={handleDeletePref}
                    colorScheme='red' ml={3}>
                    Yes
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DrawerBody>
          <DrawerFooter>
            <Button
              leftIcon={<Logout fontSize={theme.fontSize.iconLeft} />}
              onClick={handleLogout}>
              Log out
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};

export default HeaderCPT;